// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getAssetTransitReport } from '@/report/api/get-asset-transit-report';
import { useReportInterval } from '@/report/hooks/use-report-interval';
import { useQuery } from '@tanstack/react-query';

interface Options {
  start?: string;
  end?: string;
  enabled?: boolean;
}

/**
 * A React hook that manages the state of a request for a transit report of
 * assets for the current user's organization.
 *
 * @param options.start - The start of the time period. If not provided, the
 * start date is 31 days before the current date.
 * @param options.end - The end of the time period. If not provided, the end
 * date is the day before the current date.
 * @param options.enabled - Whether the query should run. Defaults to true.
 */
export function useAssetTransitReport(options: Options) {
  const interval = useReportInterval(options);

  const query = useQuery({
    refetchOnWindowFocus: false,
    queryKey: ['report', 'transit', interval.toString()],
    queryFn: () => {
      return getAssetTransitReport({
        start: interval.start.toISODate(),
        end: interval.end.toISODate(),
      });
    },
    enabled: options.enabled ?? true,
  });

  return { interval, ...query };
}
